import { StyleSheet, css } from 'aphrodite'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import LoadTable from '../components/LoadTable'
import TagHeader from '../components/TagHeader'
import { useLoadContext } from '../contexts/LoadContext'
import { api } from '../services/fetch'
import { fetchSignatureAsDataUrl } from '../services/s3Service'
import type { TagStatus } from '../types/tag'
import { loadDashboardStyles } from './LoadDashboard'

interface Tag {
  id: number
  status: string
  project?: {
    name: string
  }
  workorder?: {
    id: number
  }
  assigned_user?: {
    first_name: string
    last_name: string
    phone_number: string
    email: string
  }
  assigned_to?: {
    contact_object?: {
      name: string
      phone_number: string
      email: string
    }
  }
  site_supervisor?: {
    first_name: string
    last_name: string
    phone_number: string
    email: string
  }
  driver_signature?: string
  supervisor_signature?: string
  // New fields from updated API
  driver_name?: string
  driver_email?: string
  driver_phone?: string
  supervisor_name?: string
  supervisor_email?: string
  supervisor_phone?: string
}

interface PrivateConfirmationData {
  tag: Tag
  supervisor_name: string
  supervisor_email: string
  supervisor_phone?: string
  supervisor_type: 'assigned' | 'custom'
  accessed_at: string | null
  access_count: number
  expires_at: string
  is_expired: boolean
}

const PrivateConfirmationPage: React.FC = () => {
  const { confirmationUuid } = useParams<{ confirmationUuid: string }>()
  const navigate = useNavigate()
  const { getLoadsByTag } = useLoadContext()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [confirmationData, setConfirmationData] =
    useState<PrivateConfirmationData | null>(null)
  const [driverSignature, setDriverSignature] = useState<string | null>(null)
  const [supervisorSignature, setSupervisorSignature] = useState<string | null>(
    null,
  )
  const contentRef = useRef<HTMLDivElement>(null)
  const [driverSignatureUrl, setDriverSignatureUrl] = useState<
    string | undefined
  >()
  const [supervisorSignatureUrl, setSupervisorSignatureUrl] = useState<
    string | undefined
  >()
  const [driverSigLoading, setDriverSigLoading] = useState(false)
  const [supervisorSigLoading, setSupervisorSigLoading] = useState(false)
  const [driverSigError, setDriverSigError] = useState(false)
  const [supervisorSigError, setSupervisorSigError] = useState(false)

  useEffect(() => {
    if (!confirmationUuid) {
      setError('Confirmation ID is missing.')
      setLoading(false)
      return
    }

    const fetchConfirmationDetails = async () => {
      setLoading(true)
      setError(null)

      try {
        const response = await api.get(
          `/confirmation/private/${confirmationUuid}/`,
        )
        setConfirmationData(response.data)

        // Load loads for the tag
        if (response.data.tag.id) {
          getLoadsByTag(response.data.tag.id.toString())
        }
      } catch (err: unknown) {
        console.error('Failed to fetch confirmation data:', err)
        if (err && typeof err === 'object' && 'response' in err) {
          const errorResponse = err as {
            response?: { status?: number; data?: { link?: string } }
          }
          if (errorResponse.response?.status === 404) {
            setError('Confirmation link not found')
          } else if (errorResponse.response?.status === 400) {
            setError(
              errorResponse.response.data?.link ||
                'This confirmation link has expired',
            )
          } else {
            setError('Failed to load confirmation details')
          }
        } else {
          setError('Failed to load confirmation details')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchConfirmationDetails()
  }, [confirmationUuid, getLoadsByTag])

  useEffect(() => {
    if (confirmationData?.tag?.driver_signature) {
      setDriverSignature(confirmationData.tag.driver_signature)
    }
    if (confirmationData?.tag?.supervisor_signature) {
      setSupervisorSignature(confirmationData.tag.supervisor_signature)
    }
  }, [confirmationData])

  useEffect(() => {
    if (!driverSignature) return setDriverSignatureUrl(undefined)
    setDriverSigLoading(true)
    setDriverSigError(false)
    fetchSignatureAsDataUrl(driverSignature)
      .then((url) => setDriverSignatureUrl(url))
      .catch(() => setDriverSigError(true))
      .finally(() => setDriverSigLoading(false))
  }, [driverSignature])

  useEffect(() => {
    if (!supervisorSignature) return setSupervisorSignatureUrl(undefined)
    setSupervisorSigLoading(true)
    setSupervisorSigError(false)
    fetchSignatureAsDataUrl(supervisorSignature)
      .then((url) => setSupervisorSignatureUrl(url))
      .catch(() => setSupervisorSigError(true))
      .finally(() => setSupervisorSigLoading(false))
  }, [supervisorSignature])

  const handleDownloadPdf = async () => {
    if (!contentRef.current) return

    // Save the original width
    const originalWidth = contentRef.current.style.width
    // Set the width to 540px for screenshot
    contentRef.current.style.width = '540px'
    // Wait for the browser to reflow
    await new Promise((resolve) => setTimeout(resolve, 50))

    // Wait for all images (including signatures) to load
    const images = contentRef.current.querySelectorAll('img')
    await Promise.all(
      Array.from(images).map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) resolve()
            else {
              img.onload = () => resolve()
              img.onerror = () => resolve()
            }
          }),
      ),
    )

    const width = 540
    const height = Math.ceil(contentRef.current.scrollHeight) + 20 // add buffer

    const canvas = await html2canvas(contentRef.current, {
      width,
      height,
      scale: 2,
      useCORS: true,
      scrollY: -window.scrollY,
      backgroundColor: '#fff', // ensure white background
    })

    // Restore the original width
    contentRef.current.style.width = originalWidth

    const imgData = canvas.toDataURL('image/png')

    // Create PDF with the same dimensions as the content
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'px',
      format: [width, height],
    })
    pdf.addImage(imgData, 'PNG', 0, 0, width, height)
    pdf.save('confirmation.pdf')
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading confirmation details...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-6 bg-white rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-red-600 mb-4">Error</h2>
          <p className="text-gray-700">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    )
  }

  if (!confirmationData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-6 bg-white rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            No Confirmation Data
          </h2>
          <p className="text-gray-700">Could not load confirmation details.</p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    )
  }

  const { tag } = confirmationData

  return (
    <div className={css(loadDashboardStyles.pageContainer)}>
      <div ref={contentRef}>
        <TagHeader
          tagId={tag.id.toString()}
          title="Confirmation page"
          initialIsExpanded={true}
          isDetailed={true}
          status={(tag.status as TagStatus) || 'COMPLETE'}
        />

        <div className={css(loadDashboardStyles.container)}>
          <div className={css(loadDashboardStyles.tableSection)}>
            {tag.id && <LoadTable tagId={tag.id.toString()} />}
          </div>
        </div>

        <div className={css(styles.signaturesContainer)}>
          {/* Driver Signature */}
          <div className={css(styles.signatureSection)}>
            <h3 className={css(styles.signatureTitle)}>Driver Signature</h3>
            <div className={css(styles.supervisorInfo)}>
              <div className={css(styles.inputGroup)}>
                <label className={css(styles.label)}>Name</label>
                <div className={css(styles.inputWrapper)}>
                  <i className={`fa-solid fa-user ${css(styles.inputIcon)}`} />
                  <input
                    type="text"
                    value={tag.driver_name || ''}
                    className={css(styles.input)}
                    disabled
                  />
                </div>
              </div>

              <div className={css(styles.inputGroup)}>
                <label className={css(styles.label)}>Phone</label>
                <div className={css(styles.inputWrapper)}>
                  <i className={`fa-solid fa-phone ${css(styles.inputIcon)}`} />
                  <input
                    type="text"
                    value={tag.driver_phone || ''}
                    className={css(styles.input)}
                    disabled
                  />
                </div>
              </div>

              <div className={css(styles.inputGroup)}>
                <label className={css(styles.label)}>Email</label>
                <div className={css(styles.inputWrapper)}>
                  <i
                    className={`fa-solid fa-envelope ${css(styles.inputIcon)}`}
                  />
                  <input
                    type="email"
                    value={tag.driver_email || ''}
                    className={css(styles.input)}
                    disabled
                  />
                </div>
              </div>
            </div>
            <div className={css(styles.confirmationTextContainer)}>
              <p className={css(styles.confirmationText)}>
                I confirm loads have been delivered:
              </p>
            </div>
            {driverSigLoading ? (
              <div className={css(styles.noSignature)}>
                Loading driver signature...
              </div>
            ) : driverSigError ? (
              <div className={css(styles.noSignature)}>
                Failed to load driver signature
              </div>
            ) : driverSignatureUrl ? (
              <img
                src={driverSignatureUrl}
                alt="Driver Signature"
                className={css(styles.signatureImage)}
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                  setDriverSigError(true)
                }}
              />
            ) : (
              <div className={css(styles.noSignature)}>
                No driver signature available
              </div>
            )}
          </div>

          {/* Supervisor Signature */}
          <div className={css(styles.signatureSection)}>
            <h3 className={css(styles.signatureTitle)}>
              Site Supervisor Signature
            </h3>
            <div className={css(styles.supervisorInfo)}>
              <div className={css(styles.inputGroup)}>
                <label className={css(styles.label)}>Name</label>
                <div className={css(styles.inputWrapper)}>
                  <i className={`fa-solid fa-user ${css(styles.inputIcon)}`} />
                  <input
                    type="text"
                    value={confirmationData?.supervisor_name || ''}
                    className={css(styles.input)}
                    disabled
                  />
                </div>
              </div>

              <div className={css(styles.inputGroup)}>
                <label className={css(styles.label)}>Phone</label>
                <div className={css(styles.inputWrapper)}>
                  <i className={`fa-solid fa-phone ${css(styles.inputIcon)}`} />
                  <input
                    type="text"
                    value={confirmationData?.supervisor_phone || ''}
                    className={css(styles.input)}
                    disabled
                  />
                </div>
              </div>

              <div className={css(styles.inputGroup)}>
                <label className={css(styles.label)}>Email</label>
                <div className={css(styles.inputWrapper)}>
                  <i
                    className={`fa-solid fa-envelope ${css(styles.inputIcon)}`}
                  />
                  <input
                    type="email"
                    value={confirmationData?.supervisor_email || ''}
                    className={css(styles.input)}
                    disabled
                  />
                </div>
              </div>
            </div>
            <div className={css(styles.confirmationTextContainer)}>
              <p className={css(styles.confirmationText)}>
                I confirm loads have been received:
              </p>
            </div>
            {supervisorSigLoading ? (
              <div className={css(styles.noSignature)}>
                Loading supervisor signature...
              </div>
            ) : supervisorSigError ? (
              <div className={css(styles.noSignature)}>
                Failed to load site supervisor signature
              </div>
            ) : supervisorSignatureUrl ? (
              <img
                src={supervisorSignatureUrl}
                alt="Site Supervisor Signature"
                className={css(styles.signatureImage)}
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                  setSupervisorSigError(true)
                }}
              />
            ) : (
              <div className={css(styles.noSignature)}>
                No site supervisor signature available
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            margin: '24px 0',
          }}
        >
          <button
            className={css(styles.downloadButton)}
            onClick={handleDownloadPdf}
          >
            <i className="fa-solid fa-download" style={{ marginRight: 8 }} />
            Download PDF
          </button>
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  signaturesContainer: {
    display: 'flex',
    gap: '0px',
    flexWrap: 'wrap',
    margin: '20px 0',
    marginTop: '-136px',
  },
  signatureSection: {
    flex: 1,
    minWidth: '300px',
    padding: 16,
    borderRadius: 8,
  },
  signatureTitle: {
    fontSize: '16px',
    fontWeight: 500,
  },
  signatureImage: {
    maxWidth: '100%',
    height: 'auto',
    border: '1px solid #ddd',
    borderRadius: 4,
    marginTop: '8px',
  },
  noSignature: {
    padding: 20,
    textAlign: 'center',
    color: '#666',
    backgroundColor: '#fff',
    border: '1px dashed #ddd',
    borderRadius: 4,
    marginTop: '8px',
  },
  supervisorInfo: {
    paddingTop: '8px',
    paddingBottom: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '150%',
    color: '#111928',
  },
  inputWrapper: {
    position: 'relative',
    width: '100%',
  },
  inputIcon: {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
    color: 'rgb(156, 163, 175)',
  },
  input: {
    boxSizing: 'border-box',
    width: '100%',
    height: '36px',
    lineHeight: '36px',
    padding: '0 12px',
    paddingLeft: '38px',
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'Arial, Helvetica, sans-serif',
    color: '#000000',
    ':disabled': {
      backgroundColor: '#F3F4F6',
      cursor: 'not-allowed',
    },
  },
  confirmationTextContainer: {
    padding: '0 4px',
    marginTop: 8,
  },
  confirmationText: {
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '150%',
    color: '#111928',
  },
  downloadButton: {
    backgroundColor: '#10B981',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minHeight: '48px', // Ensure minimum touch target size for mobile
    width: '100%',
    maxWidth: '300px',
    justifyContent: 'center',
    ':hover': {
      backgroundColor: '#059669',
    },
    '@media (max-width: 768px)': {
      fontSize: '14px',
      padding: '10px 20px',
      minHeight: '44px',
    },
  },
})

export default PrivateConfirmationPage
